/**
 * Educational Content Agent
 * Provides farming tips, gardening advice, and agricultural education
 */

interface EducationalContent {
  id: string;
  title: string;
  category: 'gardening' | 'farming' | 'cooking' | 'preservation' | 'sustainability';
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  content: string;
  tips: string[];
  resources: string[];
  relatedTopics: string[];
}

interface GardeningTip {
  season: string;
  product: string;
  tip: string;
  difficulty: 'easy' | 'medium' | 'hard';
}

interface CropGuide {
  crop: string;
  plantingTime: string[];
  harvestTime: string[];
  growingConditions: {
    sunlight: 'full' | 'partial' | 'shade';
    water: 'low' | 'medium' | 'high';
    soilType: string;
    spacing: string;
  };
  companionPlants: string[];
  avoid: string[];
  commonPests: string[];
  tips: string[];
}

class EducationalContentAgent {
  private content: Map<string, EducationalContent>;
  private cropGuides: Map<string, CropGuide>;
  private seasonalTips: GardeningTip[];

  constructor() {
    this.content = this.initializeContent();
    this.cropGuides = this.initializeCropGuides();
    this.seasonalTips = this.initializeSeasonalTips();
  }

  /**
   * Get educational content by category
   * @param category Content category
   * @param limit Maximum results
   * @returns Educational content
   */
  getContentByCategory(
    category: EducationalContent['category'],
    limit: number = 5
  ): EducationalContent[] {
    return Array.from(this.content.values())
      .filter(c => c.category === category)
      .slice(0, limit);
  }

  /**
   * Get crop growing guide
   * @param crop Crop name
   * @returns Comprehensive growing guide
   */
  getCropGuide(crop: string): CropGuide | null {
    return this.cropGuides.get(crop.toLowerCase()) || null;
  }

  /**
   * Get seasonal gardening tips
   * @param month Month (0-11)
   * @param product Optional specific product
   * @returns Relevant tips for the season
   */
  getSeasonalTips(month?: number, product?: string): GardeningTip[] {
    const season = this.getSeasonFromMonth(month || new Date().getMonth());

    return this.seasonalTips.filter(tip => {
      const matchesSeason = tip.season === season;
      const matchesProduct = !product || tip.product.toLowerCase() === product.toLowerCase();
      return matchesSeason && matchesProduct;
    });
  }

  /**
   * Get personalized learning path
   * @param userLevel User skill level
   * @param interests User interests
   * @returns Recommended content sequence
   */
  getLearningPath(
    userLevel: 'beginner' | 'intermediate' | 'advanced',
    interests: string[]
  ): EducationalContent[] {
    const filtered = Array.from(this.content.values()).filter(content => {
      const matchesLevel =
        userLevel === 'beginner' ? content.difficulty !== 'advanced' :
        userLevel === 'advanced' ? true :
        content.difficulty !== 'beginner';

      const matchesInterest = interests.some(interest =>
        content.category.includes(interest) ||
        content.title.toLowerCase().includes(interest.toLowerCase())
      );

      return matchesLevel && matchesInterest;
    });

    // Sort by difficulty
    return filtered.sort((a, b) => {
      const difficultyOrder = { beginner: 0, intermediate: 1, advanced: 2 };
      return difficultyOrder[a.difficulty] - difficultyOrder[b.difficulty];
    });
  }

  /**
   * Get quick tips for a specific product
   * @param product Product name
   * @returns Quick tips array
   */
  getQuickTips(product: string): string[] {
    const guide = this.getCropGuide(product);
    if (guide) {
      return guide.tips;
    }

    // Fallback generic tips
    return [
      `Choose fresh, locally-grown ${product} for best flavor`,
      `Store properly to maintain freshness`,
      `Ask your local farmer about varieties`,
    ];
  }

  /**
   * Get companion planting recommendations
   * @param crop Main crop
   * @returns Companion plants and what to avoid
   */
  getCompanionPlanting(crop: string): {
    plant: string;
    companionPlants: string[];
    avoid: string[];
    reasons: string[];
  } | null {
    const guide = this.getCropGuide(crop);
    if (!guide) return null;

    return {
      plant: guide.crop,
      companionPlants: guide.companionPlants,
      avoid: guide.avoid,
      reasons: this.getCompanionReasons(guide.crop),
    };
  }

  /**
   * Get pest management advice
   * @param crop Crop name
   * @param pest Optional specific pest
   * @returns Organic pest management tips
   */
  getPestManagement(crop: string, pest?: string): Array<{
    pest: string;
    prevention: string[];
    organicControls: string[];
  }> {
    const guide = this.getCropGuide(crop);
    if (!guide) return [];

    return guide.commonPests.map(p => ({
      pest: p,
      prevention: this.getPreventionTips(p),
      organicControls: this.getOrganicControls(p),
    }));
  }

  // Initialization Methods

  private initializeContent(): Map<string, EducationalContent> {
    const content = new Map<string, EducationalContent>();

    content.set('tomato-growing', {
      id: 'tomato-growing',
      title: 'Complete Guide to Growing Tomatoes in Florida',
      category: 'gardening',
      difficulty: 'beginner',
      content: 'Tomatoes thrive in Florida during the fall and spring seasons. Plant in September-November or February-March for best results.',
      tips: [
        'Choose disease-resistant varieties',
        'Stake or cage plants for support',
        'Water consistently to prevent cracking',
        'Mulch to retain moisture',
      ],
      resources: ['UF/IFAS Tomato Growing Guide', 'Florida Vegetable Gardening Guide'],
      relatedTopics: ['Pest Management', 'Organic Fertilization', 'Container Gardening'],
    });

    content.set('composting-basics', {
      id: 'composting-basics',
      title: 'Composting 101: Turn Waste into Garden Gold',
      category: 'sustainability',
      difficulty: 'beginner',
      content: 'Composting is the process of decomposing organic matter to create nutrient-rich soil amendment.',
      tips: [
        'Balance green (nitrogen) and brown (carbon) materials',
        'Keep pile moist but not soggy',
        'Turn regularly for faster decomposition',
        'Avoid meat, dairy, and oily foods',
      ],
      resources: ['Florida Composting Guide', 'Backyard Composting Basics'],
      relatedTopics: ['Soil Health', 'Waste Reduction', 'Organic Gardening'],
    });

    content.set('food-preservation', {
      id: 'food-preservation',
      title: 'Preserving Your Harvest: Canning, Freezing, and Drying',
      category: 'preservation',
      difficulty: 'intermediate',
      content: 'Learn safe food preservation methods to enjoy your harvest year-round.',
      tips: [
        'Follow tested recipes for safe canning',
        'Blanch vegetables before freezing',
        'Use proper equipment and techniques',
        'Label and date all preserved foods',
      ],
      resources: ['USDA Canning Guide', 'Ball Blue Book'],
      relatedTopics: ['Food Safety', 'Seasonal Cooking', 'Meal Planning'],
    });

    return content;
  }

  private initializeCropGuides(): Map<string, CropGuide> {
    const guides = new Map<string, CropGuide>();

    guides.set('tomatoes', {
      crop: 'Tomatoes',
      plantingTime: ['September', 'October', 'November', 'February', 'March'],
      harvestTime: ['December', 'January', 'May', 'June'],
      growingConditions: {
        sunlight: 'full',
        water: 'medium',
        soilType: 'Well-drained, rich in organic matter',
        spacing: '24-36 inches apart',
      },
      companionPlants: ['Basil', 'Carrots', 'Onions', 'Marigolds'],
      avoid: ['Cabbage', 'Corn', 'Fennel'],
      commonPests: ['Tomato Hornworm', 'Aphids', 'Whiteflies'],
      tips: [
        'Prune suckers for larger fruit',
        'Apply calcium to prevent blossom end rot',
        'Rotate crops to prevent disease',
        'Mulch to conserve moisture',
      ],
    });

    guides.set('lettuce', {
      crop: 'Lettuce',
      plantingTime: ['October', 'November', 'December', 'January', 'February'],
      harvestTime: ['December', 'January', 'February', 'March', 'April'],
      growingConditions: {
        sunlight: 'partial',
        water: 'high',
        soilType: 'Loose, well-drained',
        spacing: '6-12 inches apart',
      },
      companionPlants: ['Carrots', 'Radishes', 'Strawberries', 'Cucumbers'],
      avoid: ['Celery', 'Cabbage'],
      commonPests: ['Aphids', 'Slugs', 'Cutworms'],
      tips: [
        'Harvest in the morning for best flavor',
        'Provide afternoon shade in warmer months',
        'Succession plant every 2 weeks',
        'Keep soil consistently moist',
      ],
    });

    guides.set('strawberries', {
      crop: 'Strawberries',
      plantingTime: ['October', 'November'],
      harvestTime: ['January', 'February', 'March'],
      growingConditions: {
        sunlight: 'full',
        water: 'medium',
        soilType: 'Sandy, acidic pH 5.5-6.5',
        spacing: '12-18 inches apart',
      },
      companionPlants: ['Lettuce', 'Spinach', 'Beans', 'Thyme'],
      avoid: ['Cabbage family', 'Tomatoes'],
      commonPests: ['Spider Mites', 'Slugs', 'Birds'],
      tips: [
        'Remove runners for larger berries',
        'Use raised beds or mounds for drainage',
        'Net plants to protect from birds',
        'Replace plants every 2-3 years',
      ],
    });

    return guides;
  }

  private initializeSeasonalTips(): GardeningTip[] {
    return [
      {
        season: 'Winter',
        product: 'Strawberries',
        tip: 'Peak strawberry season! Harvest regularly and remove runners.',
        difficulty: 'easy',
      },
      {
        season: 'Winter',
        product: 'Lettuce',
        tip: 'Perfect weather for leafy greens. Plant succession crops.',
        difficulty: 'easy',
      },
      {
        season: 'Spring',
        product: 'Tomatoes',
        tip: 'Last chance to plant spring tomatoes. Stake early!',
        difficulty: 'medium',
      },
      {
        season: 'Summer',
        product: 'Peppers',
        tip: 'Hot peppers love the heat. Provide afternoon shade.',
        difficulty: 'medium',
      },
      {
        season: 'Fall',
        product: 'Tomatoes',
        tip: 'Prime tomato planting season begins. Choose disease-resistant varieties.',
        difficulty: 'easy',
      },
    ];
  }

  // Helper Methods

  private getSeasonFromMonth(month: number): string {
    if ([11, 0, 1].includes(month)) return 'Winter';
    if ([2, 3, 4].includes(month)) return 'Spring';
    if ([5, 6, 7].includes(month)) return 'Summer';
    return 'Fall';
  }

  private getCompanionReasons(crop: string): string[] {
    const reasons: Record<string, string[]> = {
      Tomatoes: [
        'Basil repels pests and improves flavor',
        'Marigolds deter nematodes',
        'Carrots help aerate soil',
      ],
      Lettuce: [
        'Radishes mark rows and mature quickly',
        'Strawberries provide ground cover',
        'Carrots maximize space usage',
      ],
    };
    return reasons[crop] || ['Companion plants benefit each other through pest control or nutrient sharing'];
  }

  private getPreventionTips(pest: string): string[] {
    const tips: Record<string, string[]> = {
      'Tomato Hornworm': [
        'Inspect plants regularly',
        'Hand-pick worms early morning',
        'Plant marigolds as a trap crop',
      ],
      Aphids: [
        'Spray with strong water stream',
        'Encourage beneficial insects',
        'Use reflective mulch',
      ],
      Slugs: [
        'Remove hiding places',
        'Water in morning to reduce moisture',
        'Use copper barriers',
      ],
    };
    return tips[pest] || ['Monitor regularly', 'Maintain plant health', 'Remove affected parts'];
  }

  private getOrganicControls(pest: string): string[] {
    const controls: Record<string, string[]> = {
      'Tomato Hornworm': ['Bacillus thuringiensis (Bt)', 'Handpicking', 'Parasitic wasps'],
      Aphids: ['Insecticidal soap', 'Neem oil', 'Ladybugs'],
      Slugs: ['Beer traps', 'Diatomaceous earth', 'Iron phosphate bait'],
    };
    return controls[pest] || ['Neem oil spray', 'Insecticidal soap', 'Manual removal'];
  }
}

export const educationalContentAgent = new EducationalContentAgent();
export default educationalContentAgent;
